import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import useEscapeKey from "../../../hooks/useEscapeKey.tsx";
import Swal from "sweetalert2";

type CreateAdminModalProps = {
    baseUrl: string,
    client: any,
    currentUser: any,
    open: boolean,
    setOpen: (check: boolean) => void,
}

export default function CreateAdminModal({ baseUrl, client, currentUser, open, setOpen }: CreateAdminModalProps) {
    const onClose = () => {
        setOpen(false);
    }
    useEscapeKey(true, onClose);
    const [lang, setLang] = useState("NO");
    const [profileImage, setProfileImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);


    const fileInputRef = useRef(null);

    const t = {
        NO: {
            title: "Ny adminbruker",
            subtitle: "Personlig informasjon",
            description: "Fyll inn grunnleggende personopplysninger for ny adminbruker.",
            firstName: "Fornavn",
            lastName: "Etternavn",
            email: "E-postadresse",
            autoCreated: "Opprettes automatisk",
            emailHint: "Genereres automatisk fra fornavn og etternavn.",
            recoveryEmail: "Gjenopprettingse-post",
            recoveryHint: "Brukes for kontogjenoppretting og sikker verifisering.",
            password: "Passord",
            passwordHint: "Skal inneholde 1 stor bokstav, 1 liten bokstav, 1 tall og 1 spesialtegn, og minimumslengden skal være 8 tegn",
            phone: "Mobilnummer",
            role: "Stilling / rolle",
            selectRole: "Velg rolle",
            admin: "Admin",
            support: "Support",
            upload: "Last opp profilbilde",
            uploadHint: "PNG, JPG eller WEBP. Klikk for å velge fil.",
            changeImage: "Bytt bilde",
            removeImage: "Fjern bilde",
            cancel: "Avbryt",
            save: "Lagre adminbruker",
            saving: "Lagrer...",
            ssoTitle: "Passord håndteres via SSO",
            ssoDesc: "Innlogging og autentisering styres via Single Sign-On."
        },
        EN: {
            title: "New admin user",
            subtitle: "Personal information",
            description: "Enter basic personal details for the new admin user.",
            firstName: "First name",
            lastName: "Last name",
            email: "Email address",
            autoCreated: "Auto-generated",
            emailHint: "Generated automatically from first and last name.",
            recoveryEmail: "Recovery email",
            recoveryHint: "Used for account recovery and verification.",
            password: "Password",
            passwordHint: "Should contain 1 uppercase, 1 lowercase, 1 number and 1 special character and minimum length should be 8 characters",
            phone: "Mobile number",
            role: "Role",
            selectRole: "Select role",
            admin: "Admin",
            support: "Support",
            upload: "Upload profile image",
            uploadHint: "PNG, JPG or WEBP. Click to choose a file.",
            changeImage: "Change image",
            removeImage: "Remove image",
            cancel: "Cancel",
            save: "Save admin user",
            saving: "Saving...",
            ssoTitle: "Password handled via SSO",
            ssoDesc: "Authentication is handled through Single Sign-On."
        }
    };

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [generatedEmail, setGeneratedEmail] = useState("@chamcore.eu");
    const [recoveryEmail, setRecoveryEmail] = useState("");
    const [phone, setPhone] = useState("+47");
    const [password, setPassword] = useState("");

    const [role, setRole] = useState("");
    const [errors, setErrors] = useState<{ role?: string, password?: string }>({});

    const txt = t[lang];

    const handleProfileUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // store actual file (important for backend)
        setProfileImage(file);

        // create preview separately
        const imageUrl = URL.createObjectURL(file);
        setPreviewUrl(imageUrl);
    };

    const removeProfileImage = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        setProfileImage(null);
        setPreviewUrl(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const normalizeString = (str: string) => {
        return str
            .toLowerCase()
            .normalize("NFD") // split accents (é → e + ́)
            .replace(/[\u0300-\u036f]/g, "") // remove accents
            .replace(/[^a-z\s]/g, "") // remove special chars
            .trim()
            .replace(/\s+/g, "."); // spaces → dots
    };

    const handleNameChange = (type: "first" | "last", value: string) => {
        if (type === "first") {
            setFirstName(value);
        } else {
            setLastName(value);
        }

        const fNameRaw = type === "first" ? value : firstName;
        const lNameRaw = type === "last" ? value : lastName;

        const fName = normalizeString(fNameRaw);
        const lName = normalizeString(lNameRaw);

        if (fName || lName) {
            const email = `${fName}.${lName}`
                .replace(/\.+/g, ".")   // remove double dots
                .replace(/^\.|\.$/g, "") + "@chamcore.eu";

            setGeneratedEmail(email);
        } else {
            setGeneratedEmail("@chamcore.eu");
        }
    };

    const handleSaveClicked = async () => {
        // Here you would typically send the data to your backend to create the new admin user.
        // For this example, we'll just log the details and close the modal.

        let newErrors: { role?: string, password?: string } = {};

        if (!role) {
            newErrors.role = "Role is required";
        }

        // if (!password) {
        //     newErrors.password = "Password is required";
        // }

        // const checks = getPasswordChecks(password);
        // const isValidPassword = Object.values(checks).every(Boolean);

        // if (!isValidPassword) {
        //     newErrors.password = "Invalid password format";
        // }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // setIsPasswordValid(validatePassword(password));
            return;
        }


        let formData = new FormData();
        formData.append("first_name", firstName);
        formData.append("last_name", lastName);
        formData.append("email", generatedEmail);
        formData.append("recovery_email", recoveryEmail);
        formData.append("role", role);
        formData.append("phone", phone);
        formData.append("client_uid", client.uid);
        if (profileImage) {
            formData.append("profile_picture", profileImage); // now it's a real file
        }
        try {
            setLoading(true);
            const response = await axios.post(`${baseUrl}/createAdminOrSupportUser`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            if (response.data.status === "OK") {
                Swal.fire({
                    icon: "success",
                    title: role + " Opprettet",
                    text: response.data.message,
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
        } catch (err: any) {
            console.log(err.response);
        } finally {
            setLoading(false);
        }
    }

    const validatePassword = (value: string) => {
        const regex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

        return regex.test(value);
    };

    const [isPasswordValid, setIsPasswordValid] = useState(true);

    const getPasswordChecks = (value: string) => {
        return {
            length: value.length >= 8,
            upper: /[A-Z]/.test(value),
            lower: /[a-z]/.test(value),
            number: /\d/.test(value),
            special: /[^A-Za-z\d]/.test(value),
        };
    };
    const [passwordChecks, setPasswordChecks] = useState({
        length: false,
        upper: false,
        lower: false,
        number: false,
        special: false,
    });

    const [showPassword, setShowPassword] = useState(false);

    const generateStrongPassword = () => {
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const special = "!@#$%^&*()_+[]{}|;:,.<>?";

        const all = upper + lower + numbers + special;

        let password = "";

        // ensure at least one of each
        password += upper[Math.floor(Math.random() * upper.length)];
        password += lower[Math.floor(Math.random() * lower.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += special[Math.floor(Math.random() * special.length)];

        // fill remaining length
        for (let i = 4; i < 12; i++) {
            password += all[Math.floor(Math.random() * all.length)];
        }

        // shuffle
        password = password
            .split("")
            .sort(() => Math.random() - 0.5)
            .join("");

        return password;
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 40 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 40 }}
                        transition={{ duration: 0.25 }}
                        className="w-full max-w-md max-h-[90vh] overflow-y-auto"
                    >
                        <div className="rounded-3xl border border-[#ebe7f5] bg-white shadow-[0_10px_30px_rgba(31,41,55,0.06)] overflow-hidden">
                            <div className="px-6 py-5 border-b border-[#f3effb] bg-gradient-to-r from-white to-[#faf7ff]">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a79c9]">{txt.title}</p>
                                        <h2 className="mt-1 text-xl font-semibold text-slate-900">{txt.subtitle}</h2>
                                        <p className="mt-1 text-sm text-slate-500">{txt.description}</p>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="inline-flex rounded-2xl border border-[#e9ddfb] bg-white p-1 shadow-sm">
                                            <button
                                                type="button"
                                                onClick={() => setLang("NO")}
                                                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${lang === "NO"
                                                    ? "bg-[#7c3aed] text-white shadow-[0_4px_12px_rgba(124,58,237,0.22)]"
                                                    : "text-slate-500 hover:text-slate-800"
                                                    }`}
                                            >
                                                NO
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setLang("EN")}
                                                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${lang === "EN"
                                                    ? "bg-[#7c3aed] text-white shadow-[0_4px_12px_rgba(124,58,237,0.22)]"
                                                    : "text-slate-500 hover:text-slate-800"
                                                    }`}
                                            >
                                                EN
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-6 space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{txt.firstName}</label>
                                        <input required onChange={(e) => handleNameChange("first", e.target.value)} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{txt.lastName}</label>
                                        <input required onChange={(e) => handleNameChange("last", e.target.value)} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">{txt.email}</label>
                                    <div className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm flex justify-between">
                                        <span>{generatedEmail}</span>
                                        <span className="text-xs text-[#7c3aed]">{txt.autoCreated}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{txt.emailHint}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">{txt.recoveryEmail}</label>
                                    <input required value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
                                    <p className="text-xs text-slate-500 mt-1">{txt.recoveryHint}</p>
                                </div>

                                {/* <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {txt.password}
                                    </label>

                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="********"
                                            value={password}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setPassword(value);

                                                const checks = getPasswordChecks(value);
                                                setPasswordChecks(checks);

                                                const isValid = Object.values(checks).every(Boolean);
                                                setIsPasswordValid(isValid);

                                                setErrors(prev => ({
                                                    ...prev,
                                                    password: isValid ? "" : "Invalid password format"
                                                }));
                                            }}
                                            className={`w-full rounded-2xl border px-4 py-3 text-sm pr-24 ${!isPasswordValid
                                                    ? "border-red-500"
                                                    : "border-slate-200"
                                                }`}
                                        />

                                        {/* Show/Hide Button */}
                                {/* <button
                                            type="button"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-slate-700"
                                        >
                                            {showPassword ? "Hide" : "Show"}
                                        </button>
                                    </div> */}

                                {/* Action buttons */}
                                {/* <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-slate-500">{txt.passwordHint}</p>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newPass = generateStrongPassword();
                                                setPassword(newPass);

                                                const checks = getPasswordChecks(newPass);
                                                setPasswordChecks(checks);
                                                setIsPasswordValid(true);

                                                setErrors(prev => ({ ...prev, password: "" }));
                                            }}
                                            className="text-xs text-[#7c3aed] font-medium hover:underline"
                                        >
                                            Generate strong password
                                        </button>
                                    </div> */}

                                {/* Checklist */}
                                {/* {password && (
                                        <div className="mt-2 space-y-1">
                                            {[
                                                { key: "length", label: "At least 8 characters" },
                                                { key: "upper", label: "One uppercase letter" },
                                                { key: "lower", label: "One lowercase letter" },
                                                { key: "number", label: "One number" },
                                                { key: "special", label: "One special character" },
                                            ].map((item) => {
                                                const valid = passwordChecks[item.key];

                                                return (
                                                    <div key={item.key} className="flex items-center gap-2 text-xs">
                                                        <span
                                                            className={`h-4 w-4 flex items-center justify-center rounded-full text-[10px] ${valid
                                                                    ? "bg-green-500 text-white"
                                                                    : "bg-slate-200 text-slate-500"
                                                                }`}
                                                        >
                                                            {valid ? "✓" : ""}
                                                        </span>
                                                        <span className={valid ? "text-green-600" : "text-slate-500"}>
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div> } */}

                                {/* <div className="rounded-2xl border border-[#ebe7f5] bg-[#faf7ff] px-4 py-4">
                                    <p className="text-sm font-semibold">{txt.ssoTitle}</p>
                                    <p className="text-xs text-slate-500">{txt.ssoDesc}</p>
                                </div> */}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{txt.phone}</label>
                                        <input required value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{txt.role}</label>
                                        <select
                                            required
                                            value={role}
                                            onChange={(e) => {
                                                setRole(e.target.value);
                                                setErrors(prev => ({ ...prev, role: "" })); // clear error on change
                                            }}
                                            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm">
                                            <option>{txt.selectRole}</option>
                                            <option value={"Admin"}>{txt.admin}</option>
                                            <option value={"Support"}>{txt.support}</option>
                                        </select>
                                        {errors.role && (
                                            <p className="text-xs text-red-500 mt-1">{errors.role}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        className="hidden"
                                        onChange={handleProfileUpload}
                                    />

                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full rounded-2xl border border-dashed border-[#d9cdf8] bg-[#fcfbff] px-4 py-4 text-sm font-medium text-[#7c3aed] transition hover:bg-[#f8f4ff]"
                                    >
                                        {profileImage ? txt.changeImage : txt.upload}
                                    </button>

                                    <p className="text-xs text-slate-500">{txt.uploadHint}</p>

                                    {previewUrl && (
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={previewUrl}
                                                    alt="Profile preview"
                                                    className="h-14 w-14 rounded-2xl object-cover border border-slate-200"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-800 truncate">Profile preview</p>
                                                    <p className="text-xs text-slate-500">Image uploaded successfully</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={removeProfileImage}
                                                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                                                >
                                                    {txt.removeImage}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t flex justify-between">
                                <button className="text-sm text-slate-600" onClick={() => setOpen(false)}>
                                    {txt.cancel}
                                </button>
                                <button disabled={loading} onClick={() => handleSaveClicked()} className="bg-[#7c3aed] text-white px-4 py-2 rounded-2xl text-sm">
                                    {loading ? txt.saving : txt.save}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
